/**
 * Deadline Reminder Workflow
 *
 * This workflow sends proactive deadline reminders:
 * 1. Scheduled to run daily
 * 2. Scans for upcoming task and event deadlines
 * 3. Classifies urgency using AI
 * 4. Sends targeted reminders per item
 */

import type { Workflow } from './types';

// =============================================================================
// WORKFLOW DEFINITION
// =============================================================================

export const deadlineReminderWorkflow: Workflow = {
  id: 'deadline-reminder',
  name: 'Promemoria Scadenze',
  description: 'Monitora quotidianamente le scadenze di task ed eventi, classifica l\'urgenza e invia promemoria proattivi per evitare ritardi',
  trigger: {
    type: 'schedule',
    config: {
      // Run twice daily: morning and afternoon
      cron: '0 9,15 * * *',
      timezone: 'Europe/Rome',
    },
  },
  steps: [
    {
      id: 'scan_deadlines',
      name: 'Scansiona Scadenze',
      type: 'custom',
      config: {
        // Find tasks and events with upcoming deadlines
        action: 'fetch_upcoming_deadlines',
        lookAheadDays: 7,
        includeTypes: ['task', 'event', 'milestone'],
        filters: {
          status: {
            neq: 'completed',
          },
        },
        groupBy: 'urgency',
      },
      next: ['classify_urgency'],
    },
    {
      id: 'classify_urgency',
      name: 'Classifica Urgenza',
      type: 'ai_analyze',
      config: {
        prompt: `Analizza queste scadenze imminenti e classifica l'urgenza.

Per ogni item, considera:
- Giorni mancanti alla scadenza
- Tipo di item (task critico, evento, milestone)
- Stato di completamento (se task)
- Impatto sul progetto o business

Rispondi in formato JSON:
{
  "items": [
    {
      "id": "item-id",
      "type": "task" | "event" | "milestone",
      "title": "titolo",
      "dueDate": "ISO date",
      "daysUntilDue": number,
      "urgencyLevel": "critical" | "high" | "medium" | "low",
      "reminderMessage": "messaggio personalizzato",
      "shouldNotify": boolean
    }
  ],
  "summary": {
    "critical": number,
    "high": number,
    "medium": number,
    "low": number
  }
}`,
        includeContext: true,
      },
      next: ['send_notification'],
    },
    {
      id: 'send_notification',
      name: 'Invia Promemoria',
      type: 'send_notification',
      config: {
        type: 'in_app',
        // Send individual notifications per urgent item
        mode: 'per_item',
        messageTemplate: '⏰ Promemoria: "{itemTitle}" scade tra {daysUntilDue} giorni. {reminderMessage}',
        // Filter: only send for items marked shouldNotify
        condition: {
          field: 'shouldNotify',
          operator: 'equals',
          value: true,
        },
        priority: 'high',
        action: {
          label: 'Apri',
          handler: 'open_item',
        },
        // Group notifications if too many
        groupThreshold: 5,
        groupMessage: 'Hai {count} scadenze imminenti. {critical} critiche, {high} urgenti.',
      },
    },
  ],
  enabled: true,
  version: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Calculate days until deadline
 */
export function calculateDaysUntilDeadline(
  dueDate: string,
  currentDate: Date = new Date()
): number {
  const deadline = new Date(dueDate);
  const diffTime = deadline.getTime() - currentDate.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Classify urgency level based on days until deadline
 */
export function classifyUrgency(
  daysUntil: number,
  itemType: 'task' | 'event' | 'milestone'
): 'critical' | 'high' | 'medium' | 'low' {
  // Critical: due today or overdue
  if (daysUntil <= 0) return 'critical';

  // High: due within 2 days
  if (daysUntil <= 2) return 'high';

  // Medium: due within 5 days (events) or 7 days (tasks)
  if (itemType === 'event' && daysUntil <= 5) return 'medium';
  if (itemType === 'task' && daysUntil <= 7) return 'medium';
  if (itemType === 'milestone' && daysUntil <= 7) return 'high';

  return 'low';
}

/**
 * Generate personalized reminder message
 */
export function generateReminderMessage(
  urgencyLevel: 'critical' | 'high' | 'medium' | 'low',
  itemType: 'task' | 'event' | 'milestone',
  daysUntil: number
): string {
  if (urgencyLevel === 'critical') {
    return itemType === 'task'
      ? 'Richiede attenzione immediata!'
      : 'Inizia tra poche ore!';
  }

  if (urgencyLevel === 'high') {
    return itemType === 'task'
      ? 'È il momento di completarlo.'
      : 'Preparati per questo evento.';
  }

  return 'Pianifica il tuo tempo.';
}
